import '../../../scss/hotfood/components/hotfood-fable.scss'

import {
  Body1,
  Box,
  Button,
  Card,
  Container,
  Display2,
  Flex,
  Grid,
  GridItem,
  Select,
} from '@sainsburys-tech/fable'
import { AlertCircleFilled, Delete } from '@sainsburys-tech/icons'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { isFeatureEnabled } from '../../../common/api/featureApi'
import { useApiClient } from '../../../common/AppContext/appContext'
import BackButton from '../../../common/components/BackButton/BackButton'
import ConfirmationBanner from '../../../common/components/Banners/ConfirmationBanner'
import ErrorBanner from '../../../common/components/Banners/ErrorBanner'
import ErrorRefresh from '../../../common/components/ErrorRefresh'
import Header from '../../../common/components/Header'
import ProgressLoading from '../../../common/components/ProgressLoading'
import { addBannerMessage } from '../../../common/Context/commonDispatch'
import { Context } from '../../../common/Context/context'
import { ErrorMessage } from '../../../common/enums/ErrorMessage'
import { Feature } from '../../../common/enums/FeatureEnum'
import { headerPlannerTitles } from '../../../common/enums/HeaderItems'
import { Menu } from '../../../common/enums/MenuEnum'
import { PlannerName } from '../../../common/enums/PlannerNameEnum'
import useComponentInitialization from '../../../common/hooks/useComponentInit'
import useErrorHandler from '../../../common/hooks/useErrorHandler'
import { IResult } from '../../../common/types/IResult'
import { checkStore, getUserStore } from '../../../utils/localStore'
import {
  createBatch,
  deleteBatch,
  editBatch,
  getBatch,
  getSkusPerProgram,
} from '../../api/productionApi'
import { getStore } from '../../api/storeApi'
import { ProductTypeId } from '../../enums/ProductType'
import { IBatch } from '../../types/IBatch'
import { IBatchItem } from '../../types/IBatchItem'
import { IOptions } from '../../types/IOptions'
import { ISkusPerProgram } from '../../types/ISkusPerProgram'
import BatchTable from './BatchTable'
import DeleteBatchModal from './DeleteBatchModal'

const BatchView = () => {
  const { id } = useParams()
  const batchId = parseInt(id ?? '', 10)

  const { dispatch } = useContext(Context)
  const navigate = useNavigate()
  const apiClient = useApiClient()

  const store = useMemo(() => getUserStore(), [])

  const [isDataLoading, setIsDataLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isDeleteBatchModalOpen, setIsDeleteBatchModalOpen] = useState(false)
  const [isNew, setIsNew] = useState(false)
  const [programs, setPrograms] = useState<ISkusPerProgram>({})
  const [batch, setBatch] = useState<IBatch>({
    batchId: 0,
    isUserCreated: false,
    items: [],
    programDescription: '',
    programId: 0,
    storeId: 0,
    temperaturesCount: 0,
  })
  const [initialProgramId, setInitialProgramId] = useState(0)
  const [isBatchInvalid, setIsBatchInvalid] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showZeroItems, setShowZeroItems] = useState(false)

  const [
    isGetStoreZeroErrorsFeatureEnabled,
    setIsGetStoreZeroErrorsFeatureEnabled,
  ] = useState<boolean | null>(null)

  const { handleError, resetError } = useErrorHandler(setErrorMessage)

  const { headerNavigationItems, isLoading: initLoading } =
    useComponentInitialization(
      apiClient,
      store,
      dispatch,
      PlannerName.HotFood,
      handleError,
    )

  const isLoading = initLoading || isDataLoading

  useEffect(() => {
    const loadBatchData = async () => {
      setIsDataLoading(true)
      resetError() // Clear previous errors

      try {
        const [skusData, storeData, isStoreZeroErrorsEnabled] =
          await Promise.all([
            getSkusPerProgram(apiClient, store.storeId),
            getStore(apiClient, store.storeId),
            isFeatureEnabled(apiClient, Feature.StoreZeroErrors, 0),
          ])

        if (isStoreZeroErrorsEnabled) {
          setIsGetStoreZeroErrorsFeatureEnabled(isStoreZeroErrorsEnabled)
          const storeError = checkStore(store)
          if (storeError) {
            handleError(storeError)
          }
        }

        setShowZeroItems(storeData.showZeroItems)
        setPrograms(skusData)

        // if "id" parameter doesn't exist then creating new batch, otherwise editing
        if (isNaN(batchId)) {
          setIsNew(true)
        } else {
          // get existing batch
          const existingBatch = await getBatch(apiClient, batchId)

          // create batch item for each SKU in the program.  Include any quantities that may exist
          if (skusData[existingBatch.programId]) {
            const batchItems = skusData[existingBatch.programId].skus.map(
              (sku) => {
                const existingBatchItem: IBatchItem | undefined =
                  existingBatch.items.find((s) => s.skuId === sku.skuId)

                return {
                  calculatedQuantity:
                    existingBatchItem !== undefined
                      ? existingBatchItem.calculatedQuantity
                      : 0,
                  portionSize:
                    existingBatchItem !== undefined
                      ? existingBatchItem.portionSize
                      : 0,
                  productTypeId:
                    existingBatchItem !== undefined
                      ? existingBatchItem.productTypeId
                      : undefined,
                  quantityProduced:
                    existingBatchItem !== undefined
                      ? existingBatchItem.quantityProduced
                      : 0,
                  quantitySold: 0,
                  skuId: sku.skuId,
                  skuName: sku.skuName,
                  toBeDisposedAt: new Date(),
                }
              },
            )

            setBatch({
              ...existingBatch,
              items: sortBatchItemsByProductType(batchItems),
            })
          }
          setInitialProgramId(existingBatch.programId)
        }
      } catch (e) {
        handleError(ErrorMessage.GeneralException, e as Error)
      } finally {
        setIsDataLoading(false)
      }
    }

    loadBatchData()
  }, [apiClient, store, batchId, handleError, resetError])

  // get options for "Please select a program" select box
  const programOptions = (): IOptions[] => {
    const keys: Array<{ id: string; description: string }> = []
    for (const key in programs) {
      if (Object.hasOwnProperty.call(programs, key)) {
        // add program option to list if creating new batch OR editing non-whole chicken program
        let add = isNew || (initialProgramId !== 1 && initialProgramId !== 13)

        // OR batch is Whole Chicken, in which case only add program 1 and/or 13
        add =
          add ||
          ((initialProgramId === 1 || initialProgramId === 13) &&
            (key === '1' || key === '13'))

        if (add) {
          keys.push({
            id: key,
            description: programs[parseInt(key, 10)].programDescription,
          })
        }
      }
    }

    return keys.map((key: { id: string; description: string }) => ({
      label: `${key.description}`,
      value: key.id,
    }))
  }

  // change value in "Please select a program" select box
  const onProgramChange = (value: string) => {
    const batchItems: IBatchItem[] = []
    let selectedProgramDescription = ''
    const programId = parseInt(value, 10)

    let originalValues: IBatchItem[] = []

    // only keep quantities for whole chicken programs
    if (initialProgramId === 1 || initialProgramId === 13) {
      originalValues = batch.items
    }

    if (programs[programId]) {
      const items = programs[programId].skus.map((sku) => {
        return {
          calculatedQuantity: 0,
          portionSize: 0,
          productTypeId: sku.productTypeId,
          quantityProduced:
            originalValues.filter((x) => x.skuId === sku.skuId)[0]
              ?.quantityProduced ?? 0,
          quantitySold: 0,
          skuId: sku.skuId,
          skuName: sku.skuName,
          toBeDisposedAt: new Date(),
        }
      })

      batchItems.push(...items)

      selectedProgramDescription = programs[programId].programDescription
    }
    setBatch({
      ...batch,
      items: sortBatchItemsByProductType(batchItems),
      programDescription: selectedProgramDescription,
      programId,
    })
    setIsBatchInvalid(false)
  }

  // change SKU quantity
  const onSkuQuantityChange = (skuId: number, value: number) => {
    const updatedBatchItems = batch.items.reduce(
      (acc: IBatchItem[], cur: IBatchItem) => {
        if (cur.skuId === skuId) {
          return [...acc, { ...cur, quantityProduced: value }]
        }
        return [...acc, cur]
      },
      [],
    )

    setBatch({ ...batch, items: updatedBatchItems })
    setIsBatchInvalid(false)
  }

  const onBatchDelete = () => {
    setIsDeleteBatchModalOpen(false)

    deleteBatch(apiClient, batch.batchId)
      .then((res: IResult) => {
        if (res.isSuccess) {
          dispatch(
            addBannerMessage(
              <ConfirmationBanner
                message={`Batch '${batch.programDescription}' successfully deleted.`}
              />,
            ),
          )
          navigate('/hotfoodview')
        } else {
          handleError(ErrorMessage.GeneralException)
        }
      })
      .catch((e) => handleError(ErrorMessage.GeneralException, e as Error))
  }

  const sortBatchItemsByProductType = (batchItems: IBatchItem[]) =>
    batchItems.sort(
      (a, b) =>
        (a.productTypeId as ProductTypeId) - (b.productTypeId as ProductTypeId),
    )

  const onBatchSave = () => {
    setIsSaving(true)

    if (batch.items.filter((i) => i.quantityProduced !== 0).length === 0) {
      setIsBatchInvalid(true)
      setIsSaving(false)
      return
    }

    const store = getUserStore()

    if (isNew) {
      createBatch(apiClient, {
        items: batch.items.map((item) => ({
          productTypeId: item.productTypeId,
          quantityProduced: item.quantityProduced,
          skuId: item.skuId,
        })),
        programId: batch.programId,
        storeId: store.storeId,
      })
        .then((res: IResult) => {
          if (res.isSuccess) {
            dispatch(
              addBannerMessage(
                <ConfirmationBanner
                  message={`Batch '${batch.programDescription}' was successfully added
                  to your production plan.`}
                />,
              ),
            )
            navigate('/hotfoodview')
          } else {
            handleError(ErrorMessage.GeneralException)
          }
        })
        .catch((e) => handleError(ErrorMessage.GeneralException, e as Error))
        .finally(() => setIsSaving(false))
    } else {
      editBatch(apiClient, {
        batchId: batch.batchId,
        items: batch.items.map((item) => ({
          quantityProduced: item.quantityProduced,
          skuId: item.skuId,
        })),
        programId: batch.programId,
        storeCode: parseInt(store.storeCode, 10),
      })
        .then((res: IResult) => {
          if (res.isSuccess) {
            dispatch(
              addBannerMessage(
                <ConfirmationBanner
                  message={`Batch '${batch.programDescription}' was successfully updated.`}
                />,
              ),
            )
            navigate('/hotfoodview')
          } else {
            // temporary warning (to be removed in future)
            if (batch.batchId === 0 && isGetStoreZeroErrorsFeatureEnabled)
              handleError('BatchId is currently set to 0')
            else {
              handleError(ErrorMessage.GeneralException)
            }
          }
        })
        .catch((e) => handleError(ErrorMessage.GeneralException, e as Error))
        .finally(() => setIsSaving(false))
    }
  }
  const getProgramDescription = (): string => {
    let res: string = ''
    if (readOnlyProgram()) {
      res = programs[batch.programId]?.programDescription || ''
    }
    if (batch.programId === 0) {
      res = 'Select a program'
    }
    return res
  }

  const readOnlyProgram = () =>
    !isNew &&
    batch.isUserCreated === false &&
    batch.programId > 0 &&
    initialProgramId !== 1 &&
    initialProgramId !== 13

  return (
    <div>
      {isLoading && <ProgressLoading message='Loading...' />}

      <Header
        title={headerPlannerTitles.hotfood}
        navItems={headerNavigationItems}
        activeMenuType={Menu.ProductionView}
      />
      <Container className='c-common-main-view-content' size={'xl'}>
        {errorMessage && <ErrorRefresh message={errorMessage} />}

        {!isLoading && !errorMessage && (
          <>
            {isBatchInvalid && (
              <ErrorBanner
                icon={<AlertCircleFilled />}
                message={`Please select at least one portion before ${isNew ? 'creating' : 'updating'} a batch.`}
              />
            )}

            {BackButton('/hotfoodview', 'Back to Production')}
            <Display2 style={{ marginBottom: '1.5rem', marginTop: '1.0rem' }}>
              {isNew ? 'New batch' : 'Edit batch'}
            </Display2>
            {isNew && (
              <Body1
                style={{
                  fontSize: 'var(--fable-text-body-1-font-size)',
                  marginBottom: '1.5rem',
                }}
              >
                You are creating a new batch. Select a cook program to see
                available SKU's and add them to your new batch.
              </Body1>
            )}
            <Box
              className='hotfood-batch-edit-card'
              style={{
                background: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.01)',
                padding: '2rem',
              }}
            >
              <Flex
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                style={{ marginBottom: '1.5rem' }}
              >
                <Grid cols={12}>
                  <GridItem
                    colSpan={{
                      default: 4,
                    }}
                  >
                    <Display2 style={{ margin: 0 }}>
                      {getProgramDescription()}
                    </Display2>
                    {!readOnlyProgram() && (
                      <Select
                        label='Please select a cook program'
                        name='programId'
                        placeholder='Select a program'
                        options={programOptions()}
                        value={
                          batch.programId ? batch.programId.toString() : ''
                        }
                        onChange={(value: string) => onProgramChange(value)}
                        testid='program-select'
                        style={{ minWidth: 220 }}
                      />
                    )}
                  </GridItem>
                </Grid>
              </Flex>

              {batch.programId > 0 && (
                <Card
                  orientation='vertical'
                  style={{ width: '100%', boxShadow: 'none', padding: 0 }}
                >
                  <BatchTable
                    batchItems={
                      isNew || batch.isUserCreated === true || showZeroItems
                        ? batch.items
                        : batch.items.filter(
                            (bi) =>
                              bi.calculatedQuantity > 0 ||
                              initialProgramId === 1 ||
                              initialProgramId === 13,
                          )
                    }
                    isInvalid={isBatchInvalid}
                    onChange={(skuId: number, value: number) =>
                      onSkuQuantityChange(skuId, value)
                    }
                  />
                </Card>
              )}
              <Flex
                direction='row'
                gap='md'
                justifyContent='space-between'
                style={{ marginTop: '2rem', width: '100%' }}
              >
                <Flex direction='row' gap='md'>
                  <Button
                    disabled={isSaving}
                    onClick={() => onBatchSave()}
                    testid={
                      isNew ? 'create-batch-button' : 'update-batch-button'
                    }
                    variant='primary'
                  >
                    {isNew ? 'Create batch' : 'Update batch'}
                  </Button>
                  <Button
                    variant={'secondary'}
                    style={{ backgroundColor: 'transparent' }}
                    onClick={() => navigate('/hotfoodview')}
                    testid='cancel-button'
                  >
                    Cancel
                  </Button>
                </Flex>

                {!isNew && (
                  <Button
                    className='c-batch-view-delete-button'
                    aria-label='Delete batch'
                    onClick={() => setIsDeleteBatchModalOpen(true)}
                    variant='tertiary'
                    icon={<Delete />}
                    testid='delete-button'
                    style={{
                      color: 'black',
                    }}
                  >
                    Delete Batch
                  </Button>
                )}
              </Flex>
            </Box>
            {isDeleteBatchModalOpen && (
              <DeleteBatchModal
                handleClose={() => setIsDeleteBatchModalOpen(false)}
                handleSave={onBatchDelete}
                isOpen={isDeleteBatchModalOpen}
              />
            )}
          </>
        )}
      </Container>
    </div>
  )
}

export default BatchView
